import { ExecutionContext } from '@microsoft/fast-element';
import moment from 'moment';
import { EnumTranslate } from '../services/enum.service';
import {CONTRACT_TYPE, ICON_COLORS, PROJECT_OPERATIONAL_STATUS, VPPA_RFQ_STATUS} from './enums/generic.enum';
import {Decimal} from 'decimal.js';

/**
 * Number formatter, can be used directly on ag-grid columns as valueFormatter.
 *
 * @param minDP min decimal points
 * @param maxDP max decimal points
 */
export function formatNumber(minDP = 2, maxDP = minDP) {
  return (params) => {
    if (!(params && typeof params.value === 'number')) return '';
    const lang = (navigator && navigator.language) || 'en-GB';
    return Intl.NumberFormat(lang, {
      minimumFractionDigits: minDP,
      maximumFractionDigits: maxDP,
    }).format(params.value);
  };
}

/**
 * Rounds to the number of digits specified using the base given
 *
 * @param num number that is being rounded
 * @param digits number of digits to round to
 * @param base base of number system used
 * @param rounding type of rounding should be in ['UP', 'DOWN', 'DEFAULT'] default is for stanard rounding
 */
export function roundNumber(
    num,
    digits = 2,
    base = 10,
    rounding: 'UP' | 'DOWN' | 'DEFAULT' = 'DEFAULT'
): number {
  if (Object.is(num, null ) || num == '' || num === undefined) {
      return 0
  }
  var decimal_num = new Decimal(num)
  var round_method;
  switch (rounding){
      case 'UP':
        round_method = Decimal.ROUND_UP;
        break;
      case 'DOWN':
        round_method = Decimal.ROUND_DOWN;
        break;
      case 'DEFAULT':
        round_method = Decimal.ROUND_HALF_UP;
        break;
      default:
        throw new Error('Invalid rounding option shall be in UP, DOWN or DEFAULT')
  }
  return decimal_num.toDP(digits + 1).toDP(digits, round_method).toNumber()
}

/**
 * Rounds to the number of digits specified using the base given but
 *  if the number is null or empty returns null
 */
export function roundNumberOptionalField(num, digits = 2, base = 10): string {
  const pow = Math.pow(base || 10, digits);
  return num ? (Math.round(num * pow) / pow).toString() : '';
}

/**
 *
 * @param param
 * @returns a date as string formated from timestamp number
 */
export const formatDateForInput = (param: number): string => {
  if (!(param && typeof param === 'number' && param > 0)) return '';

  return moment(param).utcOffset(0).format('YYYY-MM-DD');
};

export const formatDateLong = (param: number): string => {
  if (!(param && typeof param === 'number' && param > 0)) return '';
  const date = new Date(param);

  const formattedDate = new Intl.DateTimeFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(date);
  return formattedDate;
};

const nth = function (date) {
  const dateString = String(date);
  const last = +dateString.slice(-2);
  if (last > 3 && last < 21) return 'th';
  switch (last % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatDate = (param: any): string => {
  if (typeof param === 'string') {
    if (!param.includes('/')) return param;
  } else if (typeof param !== 'string') return param;

  const date = new Date(param).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const dateSections = date.split(' ');

  const formattedDate = `${dateSections[0]}${nth(dateSections[0])} ${
    dateSections[1]
  }, '${dateSections[2].substring(2)}`;

  return formattedDate;
};

export const formatCodDate = (param: any): string => {
  if (!param) {
    return '';
  }

  const date = new Date(param).toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  if (date == 'Invalid Date') {
    return '';
  }

  const dateSections = date.split(' ');
  dateSections[1] = dateSections[1].replace(',', '');
  const formattedDate = `${dateSections[0]} ${dateSections[1]}, ${dateSections[2]}`;

  return formattedDate;
};

export const formatMMDDYYYY = (param: any): string => {
  if (!param) {
    return '';
  }
  const date = new Date(param)?.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });

  if (date == 'Invalid Date') return '';

  return date;
};

function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

export const formatCriteriaDateMinutesPrecisionFromYear = (
  year: number,
  isFirstDay: boolean
): string => {
  if (!year) {
    return '';
  }

  let date;

  if (!isFirstDay) {
    date = new Date(year, 11, 31);
  } else {
    date = new Date(year, 0, 1);
  }

  if (!isNaN(date.getTime())) {
    return [
      date.getFullYear(),
      padTo2Digits(date.getMonth() + 1),
      padTo2Digits(date.getDate()),
    ].join('') /* +
      '-' +
      [
        padTo2Digits(date.getHours()),
        padTo2Digits(date.getMinutes())
      ].join(':')*/;
  }

  return '';
};

export function eventDetail<T = any>(ctx: ExecutionContext) {
  return (ctx.event as CustomEvent).detail as T;
}

export const getEnumReadableValue = (arr: Array<EnumTranslate>, enumValue: string) => {
  const obj = arr?.find((x) => x.ENUM_VALUE === enumValue);
  if (obj) return obj.READABLE_VALUE;
  return enumValue || '';
};

export const formatPostCodCreditAmout = (param: any): string => {
  if (param) {
    const response = '$' + param + 'k/MV';
    return response;
  }
  return 'IG';
};

export const isToday = (chatDate) => {
  const date = new Date(chatDate);
  const today = new Date();

  return (
    date.getDate() == today.getDate() &&
    date.getMonth() == today.getMonth() &&
    date.getFullYear() == today.getFullYear()
  );
};

export const formatChatDate = (param: any): string => {
  if (!param) {
    return '';
  }

  const date = new Date(param);

  const dateToString = date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  if (dateToString == 'Invalid Date') return '';

  const dateSections = dateToString.split(' ');

  const formattedDate = isToday(date)
    ? `${dateSections[3]}${dateSections[4]}`
    : `${dateSections[3]}${dateSections[4]} ${dateSections[0]}${nth(dateSections[0])} ${
        dateSections[1]
      }, '${dateSections[2].substring(2)}`;

  return formattedDate;
};

export const isYesterday = (chatDate) => {
  const date = new Date(chatDate);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() == yesterday.getDate() &&
    date.getMonth() == yesterday.getMonth() &&
    date.getFullYear() == yesterday.getFullYear()
  );
};

export function formatCascadeTimestampToDateTime(bigIntValue: any): string {
    if (bigIntValue == null) {
        return 'N/A';
    }

    try {
        const bigInt = BigInt(bigIntValue);
        const shiftedBigInt = bigInt >> BigInt(22);

        const milliseconds = Number(shiftedBigInt);
        const date = new Date(milliseconds);

        if (isNaN(date.getTime())) {
            return 'N/A';
        }

        const locale = navigator.language;
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        };

        const formattedDate = date.toLocaleString(locale, options);
        return formattedDate;
    } catch (error) {
        return 'N/A';
    }
}

export function getNumericPattern(value, defaultValue?, preValue?, postValue?) {
  let response = '';
  if (!value) {
    return defaultValue ? defaultValue : '';
  }

  if (isNaN(value)) {
    return value;
  }

  response = response.concat(preValue ? preValue : '');
  response = response.concat(value.toLocaleString());
  response = response.concat(postValue ? postValue : '');

  return response;
}

export function getNumericDecimalPattern(value, defaultValue?, preValue?, postValue?) {
  let response = '';
  if (!value) {
    return defaultValue ? defaultValue : '';
  }

  if (isNaN(value)) {
    return value;
  }

  if (value < 0) {
    response = '-';
  }
  response = response.concat(preValue ? preValue : '');
  response = response.concat(Math.abs(parseFloat(value)).toFixed(2).toLocaleString());
  response = response.concat(postValue ? postValue : '');

  return response;
}

export function getTenorRangePattern(startDate, months) {
  let formattedDate = '';
  if (!startDate) {
    return formattedDate;
  }

  const date = new Date(startDate);
  const dateString = date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  if (dateString == 'Invalid Date') return '';

  const dateSections = dateString.split(' ');

  formattedDate = `${dateSections[1]} '${dateSections[2].substring(2)}`;

  if (months && !isNaN(months)) {
    const newDate = new Date(date.setMonth(date.getMonth() + parseInt(months)));
    newDate.setUTCDate(date.getUTCDate() - 1);
    const newDateString = newDate.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });

    const newDateSections = newDateString.split(' ');

    formattedDate = formattedDate.concat(
      newDateSections ? ` - ${newDateSections[1]} '${newDateSections[2].substring(2)}` : ''
    );
  }

  return formattedDate;
}

export function withoutTime(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getYearFromDate(date: number | Date): number {
  if (!date) {
    return null;
  }
  const d = new Date(date);
  return d.getFullYear();
}

export function getFullMonthYearFromDate(param: any) {
  if (!param) {
    return '';
  }

  const date = new Date(param);

  const dateToString = new Intl.DateTimeFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);

  if (dateToString == 'Invalid Date') return '';

  const dateSections = dateToString.split(' ');

  const formattedDate = `${dateSections[0]} ${dateSections[2]}`;

  return formattedDate;
}

export function convertDateToUTC(date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
}

export function getFormatednumber(value: number) {
  if (!value || isNaN(value)) return value;
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return parseFloat(value.toFixed(2)).toLocaleString();
}

export function getStatusTranslate(value) {
  if (value === VPPA_RFQ_STATUS.ACTIVE) {
    return 'Active';
  }

  if (value === VPPA_RFQ_STATUS.EXISTING_INTEREST) {
    return 'Existing Interest';
  }

  if (value === VPPA_RFQ_STATUS.DELISTED) {
    return 'Delisted';
  }

  if (value === VPPA_RFQ_STATUS.DELETED) {
    return 'Deleted';
  }

  if (value === VPPA_RFQ_STATUS.AWAITING_KEY_METRICS) {
    return 'Awaiting Key Metrics';
  }

  return value;
}

export function getPriceRange(minNum, maxNum) {
  if (!minNum && !maxNum) {
    return '[Not Specified]';
  }
  return '$' + minNum + ' - $' + maxNum;
}

export function getPriceValue(value, defaultValue) {
  if (!value) {
    return defaultValue;
  }
  return '$' + value;
}

export function formatPath(path: string): string {
  return path ? path.trim().replace(/\s+/g, '-').toLowerCase() : '';
}

export function getFormattedNumber(
  value: number,
  defaultValue: string = '',
  decimalPlaces: number = 2,
  preValue?: string
) {
  if (!value || isNaN(value)) {
    return defaultValue;
  }

  const formattedValue = parseFloat(value.toString()).toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  if (+formattedValue < 0) {
    return '(' + (preValue ? preValue : '') + formattedValue.substring(1) + ')';
  } else {
    return (preValue ? preValue : '') + formattedValue;
  }
}

export function getFormattedNumToFixedDecimals(
  value: number,
  defaultValue: string = '',
  decimalPlaces: number = 2,
  preValue?: string
) {
  if (!value || isNaN(value)) {
    const valueStr = String(value);
    if (valueStr.includes('$')) {
      value = parseDollarString(valueStr);
    } else {
      return defaultValue;
    }
  }

  const valueFormatted = parseFloat(value.toString()).toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  let response = '';

  response = response.concat(preValue ? preValue : '');
  response = response.concat(valueFormatted);

  return response;
}

export function getFormattedDate(value) {
  if (!value) {
    return '';
  }

  if (!isNaN(value)) {
    value = new Date(value);
  }

  let date = value.getUTCDate();
  let month = value.getUTCMonth() + 1;
  const year = value.getUTCFullYear();

  date = date.toString().padStart(2, '0');
  month = month.toString().padStart(2, '0');

  return `${month}/${date}/${year}`;
}

export function getFormattedTenorDateRange(startDate: string, months: number): string {
  let formattedDate: string = '';
  let formattedStartDate: string = '';
  let formattedEndDate: string = '';

  if (!startDate) {
    return formattedStartDate;
  }

  const date = new Date(startDate);
  const utcYear = date.getUTCFullYear();
  const utcMonth = date.getUTCMonth();
  const utcDay = date.getUTCDate();

  const utcStartDate = new Date(Date.UTC(utcYear, utcMonth, utcDay));
  formattedStartDate = getFormattedDate(utcStartDate);

  if (months && !isNaN(months)) {
    const endDate = new Date(utcStartDate.setUTCMonth(utcStartDate.getUTCMonth() + months));
    endDate.setUTCDate(date.getUTCDate() - 1);

    formattedEndDate = getFormattedDate(endDate);
    formattedDate = `${formattedStartDate} - ${formattedEndDate}`;
  }

  return formattedDate;
}

export const formatNumberToDate = (param: number | Date): string => {
  const date = new Date(param);
  const UTCDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

  const formattedDate = `${
    UTCDate.getUTCMonth() + 1
  }/${UTCDate.getUTCDate()}/${UTCDate.getUTCFullYear()}`;
  return formattedDate;
};

export function parseDollarString(dollarString: string): number {
  const numericString = dollarString.replace(/[^0-9.-]+/g, '');
  const numericValue = parseFloat(numericString);
  return numericValue;
}

export function getStatusColour(status: VPPA_RFQ_STATUS): string {
  switch (status) {
    case 'ACTIVE':
      return '#7acc79';
    case 'EXISTING_INTEREST':
      return '#ffb660';
    case 'DELISTED':
    case 'DELETED':
    case 'INSUFFICIENT_DATA':
      return '#f9644d';
    case 'AWAITING_KEY_METRICS':
      return '#879ba6';
  }
  return '';
}

export function getOrindalSuffix(num: number) {
  let firstPlace = num % 10,
    firstTwoPlaces = num % 100;
  if (firstPlace === 1 && firstTwoPlaces !== 11) {
    return num + 'st';
  }
  if (firstPlace === 2 && firstTwoPlaces !== 12) {
    return num + 'nd';
  }
  if (firstPlace === 3 && firstTwoPlaces !== 13) {
    return num + 'rd';
  }
  return num + 'th';
}

export function formatDateFullMonthName(epoch: number): string {
  if (!epoch) {
    return '';
  }
  const date = new Date(epoch);
  const month = date.toLocaleString('default', { month: 'long' });
  return `${month} ${getOrindalSuffix(date.getUTCDate())}, ${date.getUTCFullYear()}`;
}

export function getFillColorFromProjectStatus(projectStatus: string): string {
  return projectStatus === PROJECT_OPERATIONAL_STATUS.NEW_BUILD
      ? ICON_COLORS.NEW_BUILD_PROJECT_STATUS
      : ICON_COLORS.POST_BUILD_PROJECT_STATUS;
}

export function getFillColorFromContractType(contractType: CONTRACT_TYPE): string {
  if (contractType === CONTRACT_TYPE.REC) {
    return ICON_COLORS.REC_STATUS;
  } else if ([CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED, CONTRACT_TYPE.POST_BUILD_VPPA, CONTRACT_TYPE.POST_BUILD_PPA].includes(contractType)) {
      return ICON_COLORS.POST_BUILD_PROJECT_STATUS;
  } else if ([CONTRACT_TYPE.NEW_BUILD_VPPA, CONTRACT_TYPE.NEW_BUILD_PPA].includes(contractType)) {
      return ICON_COLORS.NEW_BUILD_PROJECT_STATUS;
  }
}